import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Activity, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAlertaFaixa } from './alertas/AlertasContexto';

// ⚠️ `ativas` = consultas EXECUTANDO agora; `total` = conexoes abertas (a maioria ociosa, porque
// o pool guarda a conexao por 20s depois de usar). Quem decide a cor da faixa e' o primeiro
// ([DA-696]) — ver o porque no `saude-banco.routes.ts`.
interface Saude {
  nivel: 'ok' | 'atencao' | 'critico' | 'desconhecido';
  total: number;
  ativas: number;
  em_transacao: number;
  presa_ha_seg: number;
  pool_max: number;
  max_conexoes: number;
}

// Poll curto de proposito: o valor da faixa e "ver o aperto CHEGANDO". 5 min
// (como os outros banners) seria tarde demais — a 45s da pra notar a subida.
const POLLING_MS = 45 * 1000;
const DISMISS_MS = 10 * 60 * 1000; // X esconde por 10 min (a faixa volta se piorar)

export function AlertaSaudeBancoBanner() {
  const { usuario } = useAuth();
  const [saude, setSaude] = useState<Saude | null>(null);
  const [dismissAte, setDismissAte] = useState(0);
  const dismissRef = useRef(0);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<Saude>('/api/admin/saude-banco');
        if (!cancelado) setSaude(r);
      } catch {
        // 503/erro = sem leitura; nao mostra faixa (nao quebra o Layout).
        if (!cancelado) setSaude(null);
      }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const critico = saude?.nivel === 'critico';
  const fundo = critico ? '#ef4444' : '#f59e0b';
  const borda = critico ? '#b91c1c' : '#b45309';
  const texto = '#000';

  const ativo = !!(podeVer && saude && saude.nivel !== 'ok' && saude.nivel !== 'desconhecido'
    && Date.now() >= dismissAte);
  const recolhido = useAlertaFaixa(
    'saude-banco',
    // O rotulo carrega o nivel porque "Banco" sozinho nao diz se e aperto ou travamento.
    ativo ? { rotulo: critico ? 'Banco no limite' : 'Banco apertado', bg: fundo, borda, qtd: 1 } : null,
  );

  if (!ativo || recolhido || !saude) return null;

  function fechar() {
    const ate = Date.now() + DISMISS_MS;
    dismissRef.current = ate;
    setDismissAte(ate);
  }

  const presa = saude.presa_ha_seg > 0;

  return (
    <div
      style={{
        background: fundo,
        borderLeft: `4px solid ${borda}`,
        color: texto,
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
      }}
    >
      {critico ? <AlertTriangle size={16} /> : <Activity size={16} />}
      <span style={{ fontWeight: 600 }}>
        {critico ? 'Banco no limite:' : 'Banco apertado:'}
      </span>
      {/* ⛔ O numero grande e' o que ACENDEU a faixa — `ativas`, nao `total` ([DA-696]). Enquanto
          a cor saia do total de conexoes abertas, a faixa ficava vermelha exibindo um numero que
          nao explicava o vermelho: 25/25 "em uso" com a loja parada e zero consulta rodando.
          O total continua na tela, em cinza, porque ver o pool enchendo tem valor — mas rotulado
          como ABERTAS, que e' o que ele mede. */}
      <span>
        {saude.ativas}/{saude.pool_max} consultas executando
      </span>
      <span style={{ opacity: 0.75, fontSize: 12 }}>
        ({saude.total} conex{saude.total === 1 ? 'ão' : 'ões'} abert{saude.total === 1 ? 'a' : 'as'})
      </span>
      {presa && <span>·</span>}
      {presa && (
        <span>
          {saude.em_transacao} transação{saude.em_transacao === 1 ? '' : 'ões'} presa{saude.em_transacao === 1 ? '' : 's'} há {saude.presa_ha_seg}s
        </span>
      )}
      <span style={{ marginLeft: 'auto', opacity: 0.85, fontSize: 12 }}>
        {critico ? 'a loja pode travar — fechar QRs de Pix abertos / aguardar' : 'monitorando'}
      </span>
      <button
        type="button"
        onClick={fechar}
        title="Dispensar por 10 minutos"
        style={{ background: 'transparent', border: 'none', color: texto, cursor: 'pointer', padding: 2 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
